import React from 'react';
import {
  LuActivity,
  LuBitcoin,
  LuChartNoAxesColumn,
  LuListChecks,
  LuRadar,
  LuScanSearch,
  LuWaves,
} from './icons';
import { MMEC_SIMPLE_TOUCH_MIN } from '../../theme/mmecSimpleMobile';
import type { IconComponent } from './navIcons';

export type SimpleTab = 'flow' | 'scanner' | 'gex' | 'volume' | 'elite_pulse' | 'analyze' | 'crypto';

interface TabDef {
  id: SimpleTab;
  label: string;
  labelAr: string;
  icon: IconComponent;
}

const TABS: TabDef[] = [
  { id: 'flow',        label: 'Flow',        labelAr: 'التدفق',    icon: LuWaves },
  { id: 'scanner',     label: 'Scanner',     labelAr: 'الماسح',    icon: LuScanSearch },
  { id: 'analyze',     label: 'Analyze',     labelAr: 'تحليل',     icon: LuListChecks },
  { id: 'gex',         label: 'GEX',         labelAr: 'جاما',      icon: LuActivity },
  { id: 'volume',      label: 'Volume',      labelAr: 'الحجم',     icon: LuChartNoAxesColumn },
  { id: 'elite_pulse', label: 'Elite Pulse', labelAr: 'نبض النخبة', icon: LuRadar },
  { id: 'crypto',      label: 'Crypto',      labelAr: 'الكريبتو',  icon: LuBitcoin },
];

interface MMECBottomTabBarProps {
  activeTab: SimpleTab;
  onTabChange: (tab: SimpleTab) => void;
  language?: string;
}

const css = `
  .mmec-bottom-tab-bar {
    --n: ${TABS.length};
    --i: 0;
    position: relative;
    display: flex;
    align-items: stretch;
    min-height: ${MMEC_SIMPLE_TOUCH_MIN + 8}px;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: var(--tabbar-bg);
    border-top: 1px solid var(--hair);
    flex-shrink: 0;
    touch-action: manipulation;
    font-family: var(--font-ui);
  }
  /* One indicator that glides between tabs rather than six that blink. */
  .mmec-bottom-tab-bar::before {
    content: '';
    position: absolute;
    top: -1px;
    inset-inline-start: 0;
    width: calc(100% / var(--n));
    height: 2px;
    background: var(--rail-act);
    transform: translateX(calc(var(--i) * 100%));
    transition: transform var(--dur-panel) var(--ease);
    pointer-events: none;
  }
  [dir="rtl"] .mmec-bottom-tab-bar::before {
    transform: translateX(calc(var(--i) * -100%));
  }
  .mmec-tab-btn {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 8px 4px 6px;
    min-height: ${MMEC_SIMPLE_TOUCH_MIN}px;
    color: var(--muted);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: color var(--dur-control) var(--ease);
  }
  .mmec-tab-btn:hover { color: var(--txt-2); }
  .mmec-tab-btn:active .mmec-tab-icon { transform: scale(0.92); }
  .mmec-tab-btn.is-active { color: var(--txt); }
  .mmec-tab-btn:focus-visible { outline-offset: -3px; }
  .mmec-tab-icon {
    display: grid;
    place-items: center;
    transition: transform var(--dur-control) var(--ease), color var(--dur-control) var(--ease);
  }
  .mmec-tab-btn.is-active .mmec-tab-icon { color: var(--rail-act); }
  .mmec-tab-label {
    font: 500 10.5px/1 var(--font-ui);
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
`;

export const MMECBottomTabBar: React.FC<MMECBottomTabBarProps> = ({
  activeTab,
  onTabChange,
  language = 'en',
}) => {
  const activeIndex = Math.max(0, TABS.findIndex((t) => t.id === activeTab));
  return (
    <>
      <style>{css}</style>
      <nav
        className="mmec-bottom-tab-bar"
        data-tour="mmec-bottom-tabs"
        style={{ '--i': activeIndex } as React.CSSProperties}
        aria-label={language === 'ar' ? 'الأدوات' : 'Tools'}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const label = language === 'ar' ? tab.labelAr : tab.label;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              data-tour={`mmec-tab-${tab.id}`}
              className={`mmec-tab-btn${isActive ? ' is-active' : ''}`}
              onClick={() => onTabChange(tab.id)}
              title={label}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="mmec-tab-icon">
                <Icon size={21} strokeWidth={isActive ? 2 : 1.75} aria-hidden />
              </span>
              <span className="mmec-tab-label">{label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};

export default MMECBottomTabBar;
